/**
 * A European city, in outline.
 *
 * Drawn rather than illustrated: single-weight strokes on the same 1.5px grid
 * as the Lucide icons, so it reads as part of the set instead of decoration
 * dropped on top. Shapes are generic-continental - a gothic spire, a domed
 * hall, mansard roofs, a clock tower - rather than any one city's landmarks,
 * because the product is not about Vienna specifically.
 *
 * `currentColor` throughout, so it inherits the accent and needs no separate
 * dark-mode treatment.
 */
export function Skyline({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 240 56"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {/* left: townhouses with mansard roofs */}
      <path d="M6 54V34l7-6 7 6v20" />
      <path d="M13 34v-4" />
      <path d="M20 54V38l6-4 6 4v16" />
      <path d="M10 44h6M24 44h4" />

      {/* gothic church: spire, nave, rose window */}
      <path d="M38 54V30l6-14 6 14v24" />
      <path d="M44 16V9" />
      <path d="M41 9h6" />
      <circle cx="44" cy="30" r="3" />
      <path d="M50 54V38h9v16" />

      {/* Ferris wheel - the centrepiece. A Riesenrad is the right landmark for
          a product about meeting someone: it is where people go together, and
          it reads as European without naming a city.

          Spokes at 30° intervals with cabins hung at the rim; the A-frame legs
          give it the weight the dome used to carry in the composition. */}
      {/* The rim nearly reaches the ground: you board a ferris wheel at the
          bottom of the wheel, so a small circle perched on tall legs reads as
          a crane. Legs are short and splayed, as in the reference. */}
      <circle cx="78" cy="35" r="14" />
      <circle cx="78" cy="35" r="12" strokeOpacity="0.5" />
      <circle cx="78" cy="35" r="1.6" />

      {/* Spokes: twelve thin lines as six diameters through the hub. Three
          would read as a peace sign; the density is what makes it a wheel. */}
      <g strokeWidth="0.7" strokeOpacity="0.55">
        <path d="M78 23v24" />
        <path d="M66 35h24" />
        <path d="M69.5 26.5l17 17" />
        <path d="M86.5 26.5l-17 17" />
        <path d="M72 24.9l12 20.2" />
        <path d="M84 24.9l-12 20.2" />
      </g>

      {/* Cabins around the whole rim - the ring of them is what reads as a
          ferris wheel at this size. */}
      <g strokeWidth="1.1">
        <circle cx="78" cy="21" r="1.3" />
        <circle cx="85" cy="22.9" r="1.3" />
        <circle cx="90.1" cy="28" r="1.3" />
        <circle cx="92" cy="35" r="1.3" />
        <circle cx="90.1" cy="42" r="1.3" />
        <circle cx="85" cy="47.1" r="1.3" />
        <circle cx="78" cy="49" r="1.3" />
        <circle cx="71" cy="47.1" r="1.3" />
        <circle cx="65.9" cy="42" r="1.3" />
        <circle cx="64" cy="35" r="1.3" />
        <circle cx="65.9" cy="28" r="1.3" />
        <circle cx="71" cy="22.9" r="1.3" />
      </g>

      {/* A-frame to the hub, splaying only as far as the rim allows. */}
      <path d="M78 35 70 54M78 35l8 19" />
      <path d="M73.4 46h9.2" strokeOpacity="0.6" />

      {/* clock tower */}
      <path d="M101 54V22h11v32" />
      <path d="M101 22l5.5-7 5.5 7" />
      <circle cx="106.5" cy="32" r="3.5" />
      <path d="M106.5 32v-2M106.5 32h1.5" />

      {/* terraced row, varied heights */}
      <path d="M118 54V40h8v14" />
      <path d="M126 54V34h9v20" />
      <path d="M135 54V44h7v10" />
      <path d="M121 46h2M129 40h3M129 47h3" />

      {/* second spire, slimmer */}
      <path d="M148 54V32l5-10 5 10v22" />
      <path d="M153 22v-5" />
      <circle cx="153" cy="34" r="2.5" />

      {/* wide civic block with arcade */}
      <path d="M164 54V38h20v16" />
      <path d="M164 38l10-6 10 6" />
      <path d="M169 54v-7a2.5 2.5 0 0 1 5 0v7" />
      <path d="M178 54v-7a2.5 2.5 0 0 1 5 0v7" />

      {/* right: last houses, tapering out */}
      <path d="M190 54V42l6-5 6 5v12" />
      <path d="M202 54V46h8v8" />
      <path d="M210 54V48h7v6" />
      <path d="M194 48h4" />

      {/* Trees, in the gaps between blocks rather than in a row - a European
          city has squares and courtyards inside it, not a park at the edge.
          Slightly lighter, so they sit behind the architecture. */}
      <g strokeOpacity="0.6">
        {/* broad-crowned, in the gap between the wheel and the clock tower.
            It was at x=62, where its crown overlapped the wheel's left cabin. */}
        <path d="M97 54v-4" />
        <circle cx="97" cy="47" r="3.2" />
        {/* narrow poplar between the tower and the terrace - an ellipse, so
            the row is not all the same lollipop */}
        <path d="M115 54v-6" />
        <ellipse cx="115" cy="44" rx="2.6" ry="4.4" />
        {/* pair in the square before the civic block */}
        <path d="M145 54v-4" />
        <circle cx="145" cy="47" r="3" />
        <path d="M161 54v-5" />
        <circle cx="161" cy="46" r="3.4" />
        {/* two at the far right, closing the row - a pair reads as the edge of
            a park rather than one lonely tree stranded past the last house */}
        <path d="M223 54v-5" />
        <circle cx="223" cy="45.5" r="3.6" />
        <path d="M232 54v-4" />
        <circle cx="232" cy="47" r="2.8" />
      </g>

      {/* the ground the city sits on */}
      <path d="M2 54h236" strokeOpacity="0.45" />
    </svg>
  )
}
